/**
 * Per-user display preferences — purely cosmetic/UX, never sent to
 * the backend, never gates a permission (that's `core/permissions`).
 */
import { create } from 'zustand';
import { persist } from 'zustand/middleware';
import { DEFAULT_PAGE_SIZE } from '@core/api/query-params';

export type TableDensity = 'compact' | 'standard' | 'comfortable';
export type DateFormat = 'DD/MM/YYYY' | 'MM/DD/YYYY' | 'YYYY-MM-DD';

/**
 * Application-wide text scale.
 *
 * A desk operator sits back from the monitor, and how far back is a fact about
 * their chair and their eyes — not something a designer can pick once for
 * everyone. `standard` is exactly what shipped before, so nobody's screen
 * moves unless they ask it to.
 */
export type TextSize = 'standard' | 'large' | 'x-large';

/** The `data-text-size` value for each step. `standard` writes no attribute, keeping the default path free of overrides. */
export const TEXT_SIZE_ATTRIBUTE: Record<TextSize, string | null> = {
  standard: null,
  large: 'large',
  'x-large': 'x-large',
};

/** Smallest first. The order the A- / A+ buttons walk. */
export const TEXT_SIZE_STEPS: TextSize[] = ['standard', 'large', 'x-large'];

/** Human label for the current step, for the header tooltip and screen readers. */
export const TEXT_SIZE_LABEL: Record<TextSize, string> = {
  standard: 'Standard',
  large: 'Large',
  'x-large': 'Extra large',
};

/**
 * One step up or down, CLAMPED at both ends rather than wrapping.
 *
 * Wrapping would turn the smallest text into the largest on one more click of
 * the same button, which is precisely the moment an operator is squinting and
 * least able to understand what just happened.
 *
 * There is deliberately no step BELOW `standard`. Standard is already the
 * floor that fixed the "cannot read it from a chair" complaint — 12px labels
 * at 6.05:1 — and a smaller step would hand that defect back as a setting.
 */
export function stepTextSize(current: TextSize, direction: 1 | -1): TextSize {
  const index = TEXT_SIZE_STEPS.indexOf(current);
  const next = index + direction;
  if (next < 0 || next >= TEXT_SIZE_STEPS.length) return current;
  return TEXT_SIZE_STEPS[next] as TextSize;
}

interface PreferencesState {
  tableDensity: TableDensity;
  dateFormat: DateFormat;
  defaultPageSize: number;
  locale: string;
  textSize: TextSize;
  setTextSize: (size: TextSize) => void;
  setTableDensity: (density: TableDensity) => void;
  setDateFormat: (format: DateFormat) => void;
  setDefaultPageSize: (size: number) => void;
  setLocale: (locale: string) => void;
}

export const usePreferencesStore = create<PreferencesState>()(
  persist(
    (set) => ({
      tableDensity: 'standard',
      dateFormat: 'DD/MM/YYYY',
      defaultPageSize: DEFAULT_PAGE_SIZE,
      locale: 'en-IN',
      textSize: 'standard',
      setTextSize: (textSize) => set({ textSize }),
      setTableDensity: (tableDensity) => set({ tableDensity }),
      setDateFormat: (dateFormat) => set({ dateFormat }),
      setDefaultPageSize: (defaultPageSize) => set({ defaultPageSize }),
      setLocale: (locale) => set({ locale }),
    }),
    { name: 'nexao-preferences' },
  ),
);
